import {ALERT_ICON, TICKET_ICON} from "./NotificationDotUI";
import {DtConstants} from "../../schema/DtConstants";
import { compareToAlert, compareToTicket } from "../layers/assets/TrackedElementsProvider";
import {
	getStateColor,
	getMostFrequentCustomThemeColor,
	DEFAULT_TICKET_COLOR
} from "../layers/assets/AssetsLayer";
import { FacetsScope, FacetTypes } from "../../facets/Facets";

const { StreamAlertState2State, StreamStatePriority } = DtConstants;

// Decagon size and some positioning help.
const SIZE = 24;
const MID_SIZE = SIZE * 0.5;
const BOT_BASELINE = SIZE * 0.75;
const BASE_STYLE_LAYOUT = { display: 'none', flexDirection: 'row', position: 'absolute' };

export class ClusteredCounts {
	#estimatedSize = { width: 0, height: 0 };
	dom;

	constructor(document, optStyle) {
		this.eventTargets = [];
		this.plugins = []; // plugins are handled by the parent UI element

		const el = document.createElement('div');
		Object.assign(el.style, BASE_STYLE_LAYOUT);
		optStyle && Object.assign(el.style, optStyle);

		this.first = createDecagon(document);
		this.eventTargets.push(this.first);
		el.appendChild(this.first);

		this.second = createDecagon(document);
		this.eventTargets.push(this.second);
		el.appendChild(this.second);

		this.dom = el;
		this.isVisible = false;
	}

	init(container) { container.appendChild(this.dom); }

	setVisible(visible) {
		this.dom.style.display = visible ? 'flex' : 'none';
		this.isVisible = visible;
	}

	setState(state) {
		const { alerts, streams, tickets, total } = state;
		if (alerts.count || tickets.count) {
			// There is a primary state being shown.
			config(this.first, tickets.count ? 'tickets' : 'off', tickets.count, tickets.state);
			config(this.second, alerts.count ? 'alerts' : 'off', alerts.count, alerts.state);
			return;
		}

		config(this.second, 'off');
		if (streams) {
			config(this.first, 'streams', total, streams);
			return;
		}

		config(this.first, 'assets', total);
	}

	updateFromList(deferred) {
		if (!deferred.length) {
			this.#estimatedSize.width = 0;
			this.#estimatedSize.height = 0;
			this.setVisible(false);
			return;
		}

		const state = getStateFromList(deferred);
		if (state.alerts.count && state.tickets.count) {
			this.#estimatedSize.width = SIZE * 2;
			this.#estimatedSize.height = SIZE;
		} else {
			this.#estimatedSize.width = SIZE;
			this.#estimatedSize.height = SIZE;
		}

		this.setState(state);
		this.setVisible(true);
	}

	getEstimatedSize() { return this.#estimatedSize; }
}

function config(el, type, count, optState) {
	el.dataset.hoveredType = type === 'off' ? undefined : type;

	if (type === 'off') {
		el.style.display = 'none';
		return;
	}

	let color;
	switch (type) {
		case 'alerts':
			const { alertState } = optState;
			color = getStateColor(StreamAlertState2State[alertState]);
			setDecagon(el, color, count, 'alerts');
			break;
		case 'assets':
			setDecagon(el, '#f6f6f6', count);
			break;
		case 'streams':
			color = getStateColor(optState);
			setDecagon(el, color, count);
			break;
		case 'tickets':
			setDecagon(el, optState?.decagonColor ?? DEFAULT_TICKET_COLOR, count, 'tickets');
			break;
		default:
			console.assert(false, `unexpected type ${type}`);
	}

	// confirm visible. (check first?)
	el.style.display = 'block';
}

const _state = { tickets: {}, alerts: {}, streams: -1, total: 0 };
function getStateFromList(deferred) {
	_state.alerts.state = -1;
	_state.alerts.count = 0;
	_state.streams = null;
	_state.tickets.state = null;
	_state.tickets.count = 0;

	const allTickets = [];
	let themedFacetId;
	let defaultModel;
	for (const { model, alerts, connState, tickets } of deferred) {
		if (alerts) {
			_state.alerts.count += alerts.length;
			// relies on alerts being sorted by most important representation.
			if (compareToAlert(alerts[0], _state.alerts.state) < 0) {
				_state.alerts.state = alerts[0];
			}
		}

		if (tickets) {
			_state.tickets.count += tickets.length;

			// relies on tickets being sorted by most important representation.
			if (!_state.tickets.state || compareToTicket(tickets[0], _state.tickets.state) < 0) {
				_state.tickets.state = tickets[0];
			}

			themedFacetId ??= model.facility.facetsManager.getThemedFacetDef(FacetsScope.Tickets)?.id;
			defaultModel ??= model.facility.getDefaultModel();

			// only need to collect all tickets if the themed facet is not the priority facet to get the most frequent custom theme color later
			if (themedFacetId && themedFacetId !== FacetTypes.priority) {
				allTickets.push(...tickets);
			}
		}

		if (connState) {
			_state.streams = StreamStatePriority[connState.state] > (StreamStatePriority[_state.streams] ?? -1) ? connState.state : _state.streams;
		}
	}

	// TODO: Revisit ticket color theming after AU, ideally we have the sorting account for this or get rid of "most common color" wins
	if (_state.tickets.state) {
		let decagonColor = _state.tickets.state.priorityColor;
		if (themedFacetId) {
			decagonColor = defaultModel.myData.dbId2ThemingColor?.[_state.tickets.state.dbId];
			if (themedFacetId !== FacetTypes.priority) {
				decagonColor = getMostFrequentCustomThemeColor(allTickets, defaultModel);
			}
		}
		_state.tickets.state.decagonColor = decagonColor;
	}
	// Req. say to count "derived assets", those visible due to connection state, tickets, or alerts.
	_state.total = deferred.length;
	return _state;
}

const createDecagon = (document) => {
	const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
	svg.classList.add("assets-cluster");
	svg.style.display = 'none';
	svg.setAttribute("width", String(SIZE));
	svg.setAttribute("viewBox", `0 0 ${SIZE} ${SIZE}`);
	svg.innerHTML = '<path d="M18.7432 2.7168L22.9131 8.45508V15.5439L18.7432 21.2822L12 23.4736L5.25586 21.2822L1.08691 15.5439V8.45508L5.25586 2.7168L12 0.525391L18.7432 2.7168Z" stroke="#fff"/><g></g><text x="12"></text>';

	return svg;
};

function setDecagon(el, color, count, icon) {
	const [bg, iconHost, text] = el.childNodes;
	// if icon, then everything white, otherwise text is black.
	bg.setAttribute('fill', color);

	if (icon) {
		if (icon === 'tickets') {
			iconHost.innerHTML = TICKET_ICON;
			iconHost.style.transform = 'scale(0.65) translate(9px, 3px)'
		} else if (icon === 'alerts') {
			iconHost.innerHTML = ALERT_ICON;
			iconHost.style.transform = 'scale(0.57) translate(12px, 4px)'
		}
		text.textContent = count > 99 ? '99+' : (count || '');
		text.style.fontSize = '9px';
		text.setAttribute('fill', 'white');
		text.setAttribute('y', String(BOT_BASELINE));
		return;
	}

	iconHost.innerHTML = '';
	if (count > 99) {
		text.style.fontSize = '9px';
	} else if (count > 9) {
		text.style.fontSize = '11px';
	} else {
		text.style.fontSize = '14px';
	}
	text.textContent = count > 999 ? '999+' : count;
	text.setAttribute('fill', 'black');
	text.setAttribute('y', String(MID_SIZE));
}
